import * as React from 'react';
import { IProfileResult } from '../../../../data/providers/reports/profileReportProvider';
import { Entity } from '../../../../data/types';
import { roundValue } from '../../../../utils/math';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { Card } from '../../../shared/card/Card';
import { CardHeader } from '../../../shared/card/CardHeader';
import { Metric } from '../../../shared/Metric';
import { Notable } from '../../../shared/pdf/Notable';
import { EPdfMode, isPdf, shouldInvert } from '../../../shared/pdf/Pdf';
import { colors } from '../../colors';
import { DataCell, DataColumn, LabelCell } from '../../utils/Table';

interface IProps {
  result: IProfileResult;
  pdfMode: EPdfMode;
  entity: Entity;
}

export const ProfileResult = (props: IProps): React.ReactElement => {
  const pdf = isPdf(props.pdfMode);
  const { result, entity } = props;

  const labels: React.ReactNode[] = [
    <LabelCell key='t' style={{ backgroundColor: colors[0], ...(pdf && { flex: 1 }) }}>
      <div>{`${entity.name} Mean`}</div>
      <div>
        <div>({`${result.targetCount} Tests`})</div>
        <div> {result.targetTimeframe}</div>
      </div>
    </LabelCell>,
    <LabelCell key='p' style={{ backgroundColor: colors[1], ...(pdf && { flex: 1 }) }}>
      <div>Population Mean</div>
      <div>
        <div>({`${result.populationCount} Tests`})</div>
        <div>{result.populationTimeframe}</div>
      </div>
    </LabelCell>,
    <LabelCell key='z' center style={pdf ? { flex: 1 } : undefined}>Z Score</LabelCell>,
    <LabelCell key='d' center style={pdf ? { flex: 1 } : undefined}>% Difference</LabelCell>,
    <LabelCell key='td' center last style={pdf ? { flex: 1 } : undefined}>Total Difference</LabelCell>,
  ];

  const values: React.ReactNode[] = [
    <DataCell key='t' lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.target?.mean, result.target?.roundingPrecision)}
        units={result.target?.units}
      />
    </DataCell>,
    <DataCell key='p' lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.population?.mean, result.population?.roundingPrecision)}
        units={result.population?.units}
      />
    </DataCell>,
    <DataCell key='z' lastRow={pdf}>
      <Metric style={{ alignItems: 'center' }} value={roundValue(result.zscore, 2)} />
    </DataCell>,
    <DataCell key='d' lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.percentDifference, 2)}
        units='%'
        isSecondary
      />
    </DataCell>,
    <DataCell key='td' lastInRow lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.change, result.target?.roundingPrecision)}
        units={result.target?.units}
        isSecondary
      />
    </DataCell>,
  ];

  return (
    <Notable pdf={pdf}>
      <Card
        style={{ marginTop: 'var(--grid-gap)', ...(pdf ? { height: 'auto', minHeight: 0 } : {}) }}
        inverse={shouldInvert(props.pdfMode)}
      >
        <Column>
          <CardHeader surface={24}>
            <div>
              <div>{result.testMeta}</div>
              <div>{result.metric.metric.label}</div>
            </div>
          </CardHeader>
          {pdf ? (
            <Column>
              <Row style={{ alignItems: 'stretch' }}>{labels}</Row>
              <Row style={{ alignItems: 'stretch' }}>{values}</Row>
            </Column>
          ) : (
            <Row style={{ height: '100%' }} shouldCollapse>
              {labels.map((label, i) => (
                <DataColumn key={i}>
                  {label}
                  {values[i]}
                </DataColumn>
              ))}
            </Row>
          )}
        </Column>
      </Card>
    </Notable>
  );
};
